import { useResourceDefinitions, useStore } from "ra-core";
import { ResourceMenuItem } from "react-admin";
import get from "lodash/get";
import set from "lodash/set";
import pickBy from "lodash/pickBy";
import sortBy from "lodash/sortBy";
import groupBy from "lodash/groupBy";
import flatMap from "lodash/flatMap";
import { Box } from "@mui/material";
import { Settings } from "./settings";
import { customRouteMenu, debugInfo } from "../theme/styles";
import { AccordionMenu } from "../components/accordionMenu";
import { useCustomRoutes } from "../config/customRoutes";
import { getConfig } from "../config/packageSetup";
import { MenuItem } from "../components/MenuItem";
import { MenuSpacer } from "../components/menuSpacer";
import { menu_usage_store, updateMenuUsage } from "./menuUsage";
import { getFavorites } from "../components/menuFavorites";
import { useMyContext } from "../components/myContext";

const isAvailableForMenu = (resource) => {
  const debugFilter = Settings.debugging() || !resource?.options?.debugOnly;
  const restrictedFilter =
    Settings.getDebugSetting("show_restricted_elements") ||
    !resource?.options?.isRestrictedElement;
  const availableFilter =
    resource.hasList ||
    resource.onMenu ||
    resource?.options?.isRestrictedElement;

  return availableFilter && debugFilter && restrictedFilter;
};

const getSx = (options) =>
  Object.assign(
    {},
    customRouteMenu,
    options.sx || {},
    options.debugOnly ? debugInfo : {}
  );

const renderResourceMenu = (menuUsageStore, resourceName, options) => (
  <Box
    key={resourceName}
    sx={getSx(options)}
    data-sort-order={options.sortOrder}
    onClick={() => updateMenuUsage(menuUsageStore, resourceName)}
  >
    <ResourceMenuItem name={resourceName} />
  </Box>
);

const renderCustomMenu = (menuUsageStore, route) => {
  const label =
    !route.options.spacer &&
    (route.label ||
      (route.path && route.path.replaceAll(/[^\w\d\s]/g, " ").trim()));

  return route.options.spacer ? (
    <MenuSpacer route={route} key={route.options.sortOrder} />
  ) : (
    <MenuItem
      key={route.path}
      to={route.path}
      primaryText={label}
      {...route.menuProps}
      sx={getSx(route.options || {})}
      data-sort-order={route.options.sortOrder}
      onClick={() => updateMenuUsage(menuUsageStore, label)}
    />
  );
};

const getGrouped = () => {
  const keys = getConfig().menuGroups || [];

  keys.includes("") || keys.unshift("");

  const grouped = keys.reduce((acc, key) => {
    if (typeof key == "string") {
      acc[key] = {
        label: key,
        items: [],
      };
    } else {
      acc[key.label] = { ...key, items: [] };
    }

    return acc;
  }, {});

  return grouped;
};

/**
 * Based on
 * https://github.com/marmelab/react-admin/blob/0e4c7ca1187649bbe63fd04321c20b4de0ee488b/packages/ra-ui-materialui/src/layout/ResourceMenuItems.tsx
 *
 * Edit: there is almost nothing left from the original code
 */
export const ResourceMenuItems = ({ filter, onlyThese, noFavorites }) => {
  const { acp } = useMyContext();
  const menuUsageStore = useStore(menu_usage_store, {});
  const favorites =
    noFavorites || onlyThese ? [] : getFavorites(menuUsageStore[0]);

  const resources = Object.assign(
    {},
    useResourceDefinitions(), //currently set resources (filtered)
    useCustomRoutes(acp) //raw statically defined routes (custom menus)
  );
  let menus = [];

  let available = pickBy(resources, isAvailableForMenu);
  if (filter) {
    available = pickBy(available, (resource) =>
      (resource.label || resource.name || "").includes(filter)
    );
  }

  if (onlyThese) {
    available = pickBy(available, (resource) =>
      onlyThese.includes(resource.label || resource.name)
    );
  }

  const applyGrouping = !(filter || onlyThese);

  const sorted = sortBy(available, (resource) =>
    get(resource, "options.sortOrder", 0)
  );

  let no_group = Object.values(
    pickBy(
      sorted,
      (resource) =>
        (!applyGrouping || !get(resource, "options.group")) &&
        !favorites.includes(resource.label || resource.name)
    )
  );
  const with_group = pickBy(
    sorted,
    (resource) => applyGrouping && get(resource, "options.group")
  );

  const single_group_items = flatMap(
    pickBy(groupBy(with_group, "options.group"), (group) => group.length === 1)
  );
  no_group = no_group.concat(single_group_items);
  const skip_groups = single_group_items.map((resource) =>
    get(resource, "options.group")
  );

  no_group.forEach((resource, idx) => {
    set(
      resource,
      "options.sortOrder",
      get(resource, "options.sortOrder", idx * 10)
    );
    const menu =
      resource.hasList || resource.options?.isRestrictedElement
        ? renderResourceMenu(menuUsageStore, resource.name, resource.options)
        : renderCustomMenu(menuUsageStore, resource);

    menus.push(menu);
  });

  const grouped = getGrouped();
  Object.keys(with_group).forEach((key) => {
    const resource = with_group[key];
    const menu =
      resource.hasList || resource.options?.isRestrictedElement
        ? renderResourceMenu(menuUsageStore, resource.name, resource.options)
        : renderCustomMenu(menuUsageStore, resource);

    const group = resource.options.group;

    grouped[group] = grouped[group] || { label: group, items: [] };
    grouped[group].items.push(menu);
  });

  Object.keys(grouped).forEach((groupName) => {
    const group = grouped[groupName];
    if (!group.items.length || skip_groups.includes(groupName)) {
      return;
    }

    menus.push(
      <AccordionMenu
        key={groupName}
        title={groupName}
        data-sort-order={group.sortOrder}
        icon={group.icon}
        debug={group.debug}
      >
        <Box>{group.items}</Box>
      </AccordionMenu>
    );
  });

  menus = sortBy(menus, (menu_entry) =>
    get(menu_entry, "props.data-sort-order", 1000)
  );

  return <>{menus}</>;
};
